import React, { useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from '../utils/cn';

// Single source of truth for control heights — inputs, select/date triggers
// and buttons all pull from here so a form row always lines up.
export const controlHeights = {
  sm: 'h-8',
  md: 'h-10',
};

// Padding + text of every text-like field, without the height — fields that
// grow with content (KoobInputTextarea) take these alone.
export const inputPaddingClasses = {
  sm: 'px-2.5 py-1 text-sm',
  md: 'px-3 py-2 text-base md:text-sm',
};

// Box metrics (height + padding + text) of every text-like field, so
// KoobInputText and the KoobInputDate trigger stay the same height
// (see packages/ui/README.md).
export const inputSizeClasses = {
  sm: `${controlHeights.sm} ${inputPaddingClasses.sm}`,
  md: `${controlHeights.md} ${inputPaddingClasses.md}`,
};

// Same metrics but min-height, for multi-value triggers whose tag list can
// wrap taller than one row. min-h-8/min-h-10 are spelled out because Tailwind
// only generates classes it sees literally — keep them matching controlHeights.
export const inputMinSizeClasses = {
  sm: `min-h-8 ${inputPaddingClasses.sm}`,
  md: `min-h-10 ${inputPaddingClasses.md}`,
};

// Shared border + background of every field-shaped control (text, select
// trigger, date trigger), so they can't drift to different border colors.
export const inputFrameClasses =
  'border border-solid border-input bg-background';

// Shared foundation for text-like fields (see packages/ui/README.md) —
// reuse inputVariants / inputSizeClasses / inputFrameClasses when building
// other Koob input components.
// Corner radius shared by every field-shaped control (text, select trigger,
// date trigger) — 'md' is the default, 'full' for pill-shaped filter bars.
export const inputRoundedClasses = {
  md: 'rounded-md',
  full: 'rounded-full',
};

// Focus ring / placeholder / disabled treatment shared by every field-shaped
// control, so KoobInputText and KoobInputTextarea can't drift apart.
export const inputInteractionClasses =
  'ring-offset-background transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100';

export const inputErrorClasses = 'border-red-500 ring-2 ring-red-500/20';

// Soft flag for a value that is missing but not invalid (e.g. a rate still to
// fill in): orange border, no ring.
export const inputWarningClasses = 'border-orange-400';

// Empty field that inherits a value shown as its placeholder (e.g. an
// age-group default price): dashed border.
export const inputInheritedClasses = 'border-dashed';

// Secondary value next to a primary one (e.g. a group price beside its FIT
// price): light grey background. Important, so it beats the frame's
// bg-background whatever the CSS order.
export const inputMutedClasses = '!bg-gray-50';

export const inputVariants = cva(
  `flex w-full ${inputFrameClasses} ${inputInteractionClasses} file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground`,
  {
    variants: {
      size: inputSizeClasses,
      rounded: inputRoundedClasses,
      error: {
        true: inputErrorClasses,
      },
      warning: {
        true: inputWarningClasses,
      },
      inherited: {
        true: inputInheritedClasses,
      },
      muted: {
        true: inputMutedClasses,
      },
      hasIconLeft: { true: 'pl-9' },
      hasIconRight: { true: 'pr-9' },
      // Room for a short unit text such as "JPY G".
      hasSuffix: { true: 'pr-12' },
    },
    defaultVariants: {
      size: 'md',
      rounded: 'md',
    },
  },
);

type KoobInputTextProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className' | 'size'
> &
  Omit<
    VariantProps<typeof inputVariants>,
    | 'error'
    | 'warning'
    | 'inherited'
    | 'muted'
    | 'hasIconLeft'
    | 'hasIconRight'
    | 'hasSuffix'
  > & {
    /** Not part of the API (see packages/ui/README.md) — swallowed so JS callers can't style the input. */
    className?: never;
    error?: boolean;
    /** Orange border for a missing-but-allowed value; `error` wins. */
    warning?: boolean;
    /** Dashed border: the placeholder shows an inherited value. */
    inherited?: boolean;
    /** Light grey background: a secondary value next to a primary one. */
    muted?: boolean;
    iconLeft?: React.ReactNode;
    /** Makes iconLeft a button */
    onIconLeftClick?: () => void;
    iconRight?: React.ReactNode;
    /** Makes iconRight a button (e.g. clear). type="password" gets a visibility toggle for free. */
    onIconRightClick?: () => void;
    /** Short unit text on the right (e.g. currency); wider than iconRight, not with it. */
    suffix?: React.ReactNode;
  };

function IconSlot({
  side,
  icon,
  onClick,
  disabled,
  tabIndex,
}: {
  side: 'left' | 'right';
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  tabIndex?: number;
}) {
  const position = side === 'left' ? 'left-0' : 'right-0';
  if (onClick) {
    return (
      <button
        type="button"
        tabIndex={tabIndex}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'absolute inset-y-0 flex items-center border-none bg-transparent px-3 text-muted-foreground',
          disabled
            ? 'cursor-not-allowed'
            : 'cursor-pointer hover:text-foreground',
          position,
        )}
      >
        {icon}
      </button>
    );
  }
  return (
    <span
      className={cn(
        'pointer-events-none absolute inset-y-0 flex items-center px-3 text-muted-foreground',
        position,
      )}
    >
      {icon}
    </span>
  );
}

const KoobInputText = React.forwardRef<HTMLInputElement, KoobInputTextProps>(
  (
    {
      size,
      rounded,
      error = false,
      warning = false,
      inherited = false,
      muted = false,
      iconLeft,
      onIconLeftClick,
      iconRight,
      onIconRightClick,
      suffix,
      type = 'text',
      disabled,
      // className is not part of the API (see packages/ui/README.md)
      className,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';

    const resolvedIconRight = isPassword ? (
      <i
        className={cn('fa-regular', showPassword ? 'fa-eye-slash' : 'fa-eye')}
      />
    ) : (
      iconRight
    );
    const resolvedOnIconRightClick = isPassword
      ? () => setShowPassword(!showPassword)
      : onIconRightClick;

    const input = (
      <input
        ref={ref}
        type={isPassword && showPassword ? 'text' : type}
        disabled={disabled}
        className={inputVariants({
          size,
          rounded,
          error,
          warning: warning && !error,
          inherited,
          muted,
          hasIconLeft: Boolean(iconLeft),
          hasIconRight: Boolean(resolvedIconRight),
          hasSuffix: Boolean(suffix),
        })}
        {...props}
      />
    );

    if (!iconLeft && !resolvedIconRight && !suffix) {
      return input;
    }

    return (
      <div className="relative w-full">
        {input}
        {iconLeft && (
          <IconSlot
            side="left"
            icon={iconLeft}
            onClick={onIconLeftClick}
            disabled={disabled}
          />
        )}
        {resolvedIconRight && (
          <IconSlot
            side="right"
            icon={resolvedIconRight}
            onClick={resolvedOnIconRightClick}
            disabled={disabled}
            tabIndex={isPassword ? -1 : undefined}
          />
        )}
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    );
  },
);

KoobInputText.displayName = 'KoobInputText';

export default KoobInputText;
